import type { TextItem as TextItemType } from '@webforge-survey/shared';
import { ItemShell } from './ItemShell';

interface TextItemProps {
  item: TextItemType;
  value: string;
  error?: string;
  onChange: (value: string) => void;
}

/**
 * text_short / text_long : input ou textarea.
 *
 * Si `meta.pii_risk` ou `meta.sensitive` → avertissement explicite
 * "ne pas identifier de tiers" (microcopie UI_DESIGN §6).
 *
 * Compteur de caractères discret (sans pression) ; jamais bloquant tant que
 * `maxLength` n'est pas dépassé (le moteur signalera l'erreur).
 */
export function TextItem({ item, value, error, onChange }: TextItemProps) {
  const isLong = item.type === 'text_long';
  const inputId = `input-${item.name}`;
  const piiRisk = Boolean(item.meta?.pii_risk);
  const sensitive = Boolean(item.meta?.sensitive);

  const piiWarningId = piiRisk ? `${item.name}-pii` : undefined;

  return (
    <ItemShell
      name={item.name}
      label={item.label}
      {...(item.hint !== undefined && { hint: item.hint })}
      {...(item.required !== undefined && { required: item.required })}
      {...(error !== undefined && { error })}
    >
      {piiRisk && (
        <p
          id={piiWarningId}
          className="notice text-sm mb-3 max-w-prose"
          role="note"
        >
          <span aria-hidden="true">ⓘ</span>{' '}
          Décrivez avec vos mots, sans indiquer de nom ni de détail permettant
          d'identifier quelqu'un d'autre.
          {sensitive && (
            <> Cette réponse sera chiffrée et conservée séparément.</>
          )}
        </p>
      )}
      {isLong ? (
        <textarea
          id={inputId}
          className="input-base min-h-[10rem] max-w-prose resize-y"
          value={value}
          rows={6}
          maxLength={item.maxLength}
          onChange={(e) => onChange(e.target.value)}
          aria-describedby={[piiWarningId, item.hint ? `${item.name}-hint` : null]
            .filter(Boolean)
            .join(' ') || undefined}
        />
      ) : (
        <input
          id={inputId}
          type="text"
          className="input-base max-w-prose"
          value={value}
          maxLength={item.maxLength}
          onChange={(e) => onChange(e.target.value)}
          aria-describedby={[piiWarningId, item.hint ? `${item.name}-hint` : null]
            .filter(Boolean)
            .join(' ') || undefined}
        />
      )}
      {item.maxLength && (
        <p className="text-xs text-text-muted mt-2 max-w-prose">
          {value.length} / {item.maxLength} caractères
        </p>
      )}
    </ItemShell>
  );
}
